/**
 * Audio conversion utilities using Web Audio API
 */

/**
 * Convert audio stream to PCM16/16kHz format
 */
export class AudioConverter {
  private audioContext: AudioContext | null = null;
  private sampleRate: number = 16000;

  constructor() {
    // Initialize AudioContext
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    this.audioContext = new AudioContextClass({ sampleRate: this.sampleRate });
  }

  /**
   * Convert MediaStream to PCM16/16kHz audio data
   */
  async convertStreamToPCM(stream: MediaStream): Promise<ReadableStream<Int16Array>> {
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized');
    }

    const source = this.audioContext.createMediaStreamSource(stream);
    const processor = this.audioContext.createScriptProcessor(4096, 1, 1);

    const audioContext = this.audioContext;
    return new ReadableStream({
      start(controller) {
        processor.onaudioprocess = (e) => {
          const inputData = e.inputBuffer.getChannelData(0);
          // Convert Float32Array to Int16Array (PCM16)
          const pcm16 = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            // Clamp and convert to 16-bit integer
            const s = Math.max(-1, Math.min(1, inputData[i]));
            pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          controller.enqueue(pcm16);
        };

        source.connect(processor);
        if (audioContext) {
          processor.connect(audioContext.destination);
        }
      },
      cancel() {
        processor.disconnect();
        source.disconnect();
      },
    });
  }

  /**
   * Convert AudioBuffer to PCM16 bytes
   */
  audioBufferToPCM16(audioBuffer: AudioBuffer): Int16Array {
    const length = audioBuffer.length;
    const pcm16 = new Int16Array(length);
    const channelData = audioBuffer.getChannelData(0); // Mono

    for (let i = 0; i < length; i++) {
      const s = Math.max(-1, Math.min(1, channelData[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    return pcm16;
  }

  /**
   * Convert Blob (WebM) to PCM16 using AudioContext
   */
  async convertWebMToPCM(webmBlob: Blob): Promise<Int16Array> {
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized');
    }

    const arrayBuffer = await webmBlob.arrayBuffer();
    const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
    
    // Resample to 16kHz if needed
    if (audioBuffer.sampleRate !== this.sampleRate) {
      const resampled = await this.resampleAudioBuffer(audioBuffer, this.sampleRate);
      return this.audioBufferToPCM16(resampled);
    }

    return this.audioBufferToPCM16(audioBuffer);
  }

  /**
   * Resample AudioBuffer to target sample rate
   */
  private async resampleAudioBuffer(
    audioBuffer: AudioBuffer,
    targetSampleRate: number
  ): Promise<AudioBuffer> {
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized');
    }

    const length = Math.round((audioBuffer.length * targetSampleRate) / audioBuffer.sampleRate);
    const offlineContext = new OfflineAudioContext(
      1, // channels
      length,
      targetSampleRate
    );

    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineContext.destination);
    source.start(0);

    return await offlineContext.startRendering();
  }

  /**
   * Convert Int16Array to base64 string
   */
  pcm16ToBase64(pcm16: Int16Array): string {
    const buffer = pcm16.buffer;
    const uint8Array = new Uint8Array(buffer);
    const base64 = btoa(Array.from(uint8Array, byte => String.fromCharCode(byte)).join(''));
    return base64;
  }

  dispose() {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

/**
 * Convert PCM audio data to WAV format for browser playback
 * @param pcmData - PCM audio data as Uint8Array or ArrayBuffer
 * @param sampleRate - Sample rate in Hz (default: 24000 for qwen3-tts-flash)
 * @param numChannels - Number of audio channels (default: 1 for mono)
 * @param bitsPerSample - Bits per sample (default: 16)
 * @returns WAV file as Blob
 */
export function pcmToWav(
  pcmData: Uint8Array | ArrayBuffer,
  sampleRate: number = 24000,
  numChannels: number = 1,
  bitsPerSample: number = 16
): Blob {
  const data = pcmData instanceof ArrayBuffer ? new Uint8Array(pcmData) : pcmData;
  const length = data.length;
  
  // WAV header structure
  const buffer = new ArrayBuffer(44 + length);
  const view = new DataView(buffer);
  
  // RIFF header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length, true); // File size - 8
  writeString(8, 'WAVE');
  
  // fmt chunk
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // Audio format (1 = PCM)
  view.setUint16(22, numChannels, true); // Number of channels
  view.setUint32(24, sampleRate, true); // Sample rate
  view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true); // Byte rate
  view.setUint16(32, numChannels * (bitsPerSample / 8), true); // Block align
  view.setUint16(34, bitsPerSample, true); // Bits per sample
  
  // data chunk
  writeString(36, 'data');
  view.setUint32(40, length, true); // Data chunk size
  
  // Copy PCM data
  const dataView = new Uint8Array(buffer, 44);
  dataView.set(data);
  
  return new Blob([buffer], { type: 'audio/wav' });
}

